import React, { useState, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import axios from 'axios';
import './OperationMode.css';
import WithLoading from '../WithLoading/WithLoading';
import ShowError from '../ShowError/ShowError';

class OperationModeType {
    static comfort = new OperationModeType('Comfort', 0);
    static balanced = new OperationModeType('Balanced', 1);
    static eco = new OperationModeType('Eco', 2);
    static ecoPlus = new OperationModeType('Eco-Plus', 2);

    constructor(name, eeLevel) {
        this.name = name;
        this.eeLevel = eeLevel;  // Energy Efficiency Level. Higher values means more potential for energy savings.
    }
    toString(t) {
        return t(`operation_mode.mode.${this.name.toLowerCase()}`);
    }
    toStringLowerCase() {
        return this.name.toLowerCase();
    }

    static convertFromResponse(name) {
        switch (name) {
            case 'comfort':
                return OperationModeType.comfort;
            case 'balanced':
                return OperationModeType.balanced;
            case 'eco':
                return OperationModeType.eco;
            case 'eco-plus':
                return OperationModeType.ecoPlus;
            default:
                return null;
        }
      }

    is_equal(other) {
        return this.name === other.name;
    }
}

function OperationMode({t}) {
  const [operationMode, setOperationMode] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");

  const DisplayCurrentModeWithLoading = WithLoading(DisplayCurrentMode);

  useEffect(() => {
    const fetchOperationMode = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL + `/qr/info/mode/STI%23` + encodeURIComponent(id) + '?time=' + Date.now(), {timeout: 30000});
        setOperationMode(OperationModeType.convertFromResponse(response.data.operationMode));
      } catch (error) {
        console.error('Error fetching operation mode:', error);
        setErrorMessage(t("error.loading"));
      } finally {
        setLoading(false);
      }
    };

    fetchOperationMode();
  }, [id, t]);

  if (errorMessage){
    return <ShowError error_msg={errorMessage} />
  }   
  return (
    <div className="operation-mode-container">
      <h2>{t("operation_mode")}</h2>
      <p>{t("operation_mode.explanation")}</p>
      <div align={loading ? "center" : "left"}>
        <DisplayCurrentModeWithLoading isLoading={loading} t={t} operationMode={operationMode} setShowPopup={setShowPopup} />
      </div>
      {showPopup && <UpdateModePopup t={t} setShowPopup={setShowPopup} setOperationMode={setOperationMode} setLoading={setLoading} setErrorMessage={setErrorMessage} id={id} currentMode={operationMode} />}
    </div>
  );
}

function DisplayCurrentMode({ t, operationMode, setShowPopup }) {
  return (
    <>
      <div className="mode-display">
        <p><b>{t("operation_mode.currently_active")}:</b> <span className="mode-text">{operationMode ? operationMode.toString(t) : ""}</span></p>
      </div>
      <button onClick={() => setShowPopup(true)}>{t("operation_mode.change_button")}</button>
    </>
  );
}

function UpdateModePopup({ t, setShowPopup, setOperationMode, setLoading, setErrorMessage, id, currentMode }) {
  const [newOperationMode, setNewOperationMode] = useState(currentMode);

  const handleSave = async () => {
    setShowPopup(false);
    setLoading(true);
    try {
      const response = await axios.post(process.env.REACT_APP_API_URL + `/qr/info/mode/STI%23` + encodeURIComponent(id), {
        operationMode: newOperationMode.toStringLowerCase(),
      }, {timeout: 30000});
      setOperationMode(OperationModeType.convertFromResponse(response.data.operationMode));
    } catch (error) {
      console.error('Error saving operation mode:', error);
      setErrorMessage(t("error.updating"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="popup">
      <div className="popup-content-operation">
        <h3>{t("operation_mode.popup.title")}</h3>
        <button className="reset-button" onClick={() => setNewOperationMode(OperationModeType.eco)}>{t("operation_mode.popup.reset_button")}</button>
        <div className="button-container">
          <button
            className={`mode-button ${newOperationMode.is_equal(OperationModeType.comfort) ? 'selected' : ''} comfort`}
            onClick={() => setNewOperationMode(OperationModeType.comfort)}
          >
            {OperationModeType.comfort.toString(t)}
          </button>
          <button
            className={`mode-button ${newOperationMode.is_equal(OperationModeType.balanced) ? 'selected' : ''} balanced`}
            onClick={() => setNewOperationMode(OperationModeType.balanced)}
          >
            {OperationModeType.balanced.toString(t)}
          </button>
          <button
            className={`mode-button ${newOperationMode.is_equal(OperationModeType.eco) ? 'selected' : ''} eco`}
            onClick={() => setNewOperationMode(OperationModeType.eco)}
          >
            {OperationModeType.eco.toString(t)}
          </button>
          <button
            className={`mode-button ${newOperationMode.is_equal(OperationModeType.ecoPlus) ? 'selected' : ''} eco-plus`}
            onClick={() => setNewOperationMode(OperationModeType.ecoPlus)}
          >
            {OperationModeType.ecoPlus.toString(t)}
          </button>
        </div>
        {currentMode.eeLevel > newOperationMode.eeLevel && <label className="warning-message">
            {t("operation_mode.popup.warning")}
        </label>}
        <div className="divider"></div>
        <div className="submit-cancel-container">
          <button onClick={handleSave} disabled={!newOperationMode}>{t("button.submit")}</button>
          <button onClick={() => setShowPopup(false)} className="cancel-button">{t("button.cancel")}</button>
        </div>
      </div>
    </div>
  );
}

export default withTranslation()(OperationMode);
